"use client";

import { useMemo } from "react";
import useFeatureFlag from "@/lib/feature-flags/hooks/useFeatureFlag";
import { useMonarchRule } from "@/monarch/rules/MonarchRulesProvider";
import {
  buildRecentlyViewedResolvedDisplay,
  isRecentlyViewedMonarchResolved,
  isRecentlyViewedSurfaceEnabled,
  pickRecentlyViewedMonarchOverrides,
  unwrapRecentlyViewedMonarchValue,
} from "./mergeRecentlyViewedMonarchConfig";
import { RECENTLY_VIEWED_MODULE_RULE_ID } from "./types";
import type { RecentlyViewedSurface } from "./types";

export type RecentlyViewedResolvedFromMonarch = ReturnType<
  typeof buildRecentlyViewedResolvedDisplay
>;

/**
 * Same pattern as other Monarch hooks (e.g. {@link useHomepageTrendingCourseUpdate},
 * {@link useSuperlativesModule}): expose `isLoading` and let the component decide whether to hide
 * or show fallbacks. `isSurfaceAllowed` is only meaningful once loading finishes with the flag on
 * `test`; in `legacy` mode it stays true because Monarch visibility is not applied.
 */
export type RecentlyViewedModuleConfig = {
  isLoading: boolean;
  /**
   * False when the flag is `test`, Monarch has loaded, and this surface is disabled in the rule.
   */
  isSurfaceAllowed: boolean;
  /**
   * Non-null only when the flag is `test`, Monarch has loaded, and the surface is allowed.
   * Otherwise use CMS/template props.
   */
  displayFromMonarch: RecentlyViewedResolvedFromMonarch | null;
};

/**
 * Feature flag `NEXT_PUBLIC_FEATURE_RECENTLY_VIEWED_PRODUCTS` (`test`) gates
 * fetching Cohesion rule {@link RECENTLY_VIEWED_MODULE_RULE_ID}.
 *
 * Placement stays in templates / CMS; this hook only resolves tunables + surface enablement.
 */
export function useRecentlyViewedModuleConfig(
  surface: RecentlyViewedSurface,
  callProps: {
    minItemsToRender: number;
    maxItems: number;
    displayLimit?: number;
    recencyDays: number;
    title: string;
    maxRows: number;
    initialCardsLength: number;
  },
): RecentlyViewedModuleConfig {
  const { flagValue, isLoading: flagLoading } = useFeatureFlag(
    "NEXT_PUBLIC_FEATURE_RECENTLY_VIEWED_PRODUCTS",
    "legacy",
  );

  const monarchEnabled =
    !flagLoading && flagValue !== null && flagValue === "test";

  const { isLoading: monarchLoading, value } = useMonarchRule<unknown>(
    RECENTLY_VIEWED_MODULE_RULE_ID,
    undefined,
    { enabled: monarchEnabled },
  );

  return useMemo((): RecentlyViewedModuleConfig => {
    if (flagLoading || flagValue === null) {
      return {
        isLoading: true,
        isSurfaceAllowed: true,
        displayFromMonarch: null,
      };
    }

    if (flagValue !== "test") {
      return {
        isLoading: false,
        isSurfaceAllowed: true,
        displayFromMonarch: null,
      };
    }

    if (monarchLoading) {
      return {
        isLoading: true,
        isSurfaceAllowed: true,
        displayFromMonarch: null,
      };
    }

    if (!isRecentlyViewedMonarchResolved(value)) {
      return {
        isLoading: false,
        isSurfaceAllowed: false,
        displayFromMonarch: null,
      };
    }

    const monarch = unwrapRecentlyViewedMonarchValue(value);
    const partial = pickRecentlyViewedMonarchOverrides(monarch?.data);

    if (!isRecentlyViewedSurfaceEnabled(surface, partial, monarch)) {
      return {
        isLoading: false,
        isSurfaceAllowed: false,
        displayFromMonarch: null,
      };
    }

    return {
      isLoading: false,
      isSurfaceAllowed: true,
      displayFromMonarch: buildRecentlyViewedResolvedDisplay({
        surface,
        partial,
        callProps,
      }),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callProps: only primitive fields listed above
  }, [
    flagLoading,
    flagValue,
    monarchLoading,
    value,
    surface,
    callProps.minItemsToRender,
    callProps.maxItems,
    callProps.displayLimit,
    callProps.recencyDays,
    callProps.title,
    callProps.maxRows,
    callProps.initialCardsLength,
  ]);
}
